import React, { useState } from 'react';
import { FiChevronDown, FiChevronRight, FiMoreVertical, FiEdit2, FiTrash2, FiDollarSign } from 'react-icons/fi';
import StatusDropdown from './StatusDropdown';
import PriorityDropdown from './PriorityDropdown';
import AssigneePicker from './AssigneePicker';
import './MobileBoardTaskList.css';

const formatMoney = (amount, currency = 'USD') => {
  const v = Number(amount || 0);
  if (!v) return '—';
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    }).format(v);
  } catch (_e) {
    return `${v.toFixed(0)} ${currency}`;
  }
};

const formatDate = (s) => {
  if (!s) return '—';
  try {
    return new Date(s).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch (_e) { return s; }
};

const MobileTaskRow = ({ item, token, onEdit, onDelete, onUpdate, boardBaseCurrency, paymentSummary }) => {
  const [expanded, setExpanded] = useState(false);
  const [showMenu, setShowMenu] = useState(false);

  const cur = item.currency || boardBaseCurrency || 'USD';
  const value = Number(item.value || 0);
  const paid = paymentSummary?.paid ?? 0;
  const remaining = Math.max(0, value - paid);
  const hasFinance = value > 0 || paid > 0;

  const updateField = async (field, val) => {
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/items/${item.id}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ [field]: val }),
      });
      if (onUpdate) onUpdate();
    } catch (e) { console.error(e); }
  };

  return (
    <div className={`m-task-row ${expanded ? 'expanded' : ''}`} data-testid="mobile-task-row">
      {/* Compact header (always visible) */}
      <div className="m-task-head">
        <button className="m-task-expand" onClick={() => setExpanded(!expanded)} aria-label={expanded ? 'Collapse' : 'Expand'} data-testid="mobile-task-expand">
          {expanded ? <FiChevronDown size={18} /> : <FiChevronRight size={18} />}
        </button>
        <div className="m-task-info" onClick={() => setExpanded(!expanded)}>
          <div className="m-task-name">{item.name}</div>
          {item.subtasks && item.subtasks.length > 0 && (() => {
            const done = item.subtasks.filter((s) => s.completed || s.status === 'Done').length;
            return (
              <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                ☑ {done}/{item.subtasks.length} subtasks
              </div>
            );
          })()}
          {hasFinance && (
            <div className="m-task-finance-line">
              <span className="m-task-cost">{formatMoney(value, cur)}</span>
              {remaining > 0 && remaining < value && (
                <span className="m-task-remaining">• Remaining {formatMoney(remaining, cur)}</span>
              )}
              {remaining === 0 && value > 0 && (
                <span className="m-task-paid-tag">✓ Paid</span>
              )}
            </div>
          )}
        </div>
        <div className="m-task-status-pill">
          <StatusDropdown
            value={item.status}
            onChange={(newStatus) => updateField('status', newStatus)}
          />
        </div>
        <button className="m-task-menu-btn" onClick={() => setShowMenu(!showMenu)} aria-label="Actions" data-testid="mobile-task-menu">
          <FiMoreVertical size={18} />
        </button>
        {showMenu && (
          <>
            <div className="m-menu-backdrop" onClick={() => setShowMenu(false)} />
            <div className="m-task-menu">
              <button onClick={() => { setShowMenu(false); onEdit && onEdit(item); }}>
                <FiEdit2 size={14} /> Edit
              </button>
              <button onClick={() => { setShowMenu(false); onEdit && onEdit(item, 'finance'); }}>
                <FiDollarSign size={14} /> Finance
              </button>
              <button onClick={() => { setShowMenu(false); onDelete && onDelete(item); }} style={{ color: '#E24B5B' }}>
                <FiTrash2 size={14} /> Delete
              </button>
            </div>
          </>
        )}
      </div>

      {/* Expanded details */}
      {expanded && (
        <div className="m-task-details">
          <div className="m-detail-row">
            <span className="m-detail-label">Priority</span>
            <div className="m-detail-value">
              <PriorityDropdown
                value={item.priority}
                onChange={(newPriority) => updateField('priority', newPriority)}
              />
            </div>
          </div>
          <div className="m-detail-row">
            <span className="m-detail-label">Assignees</span>
            <div className="m-detail-value">
              <AssigneePicker
                assignees={item.assignees || []}
                onChange={(newAssignees) => updateField('assignees', newAssignees)}
                token={token}
              />
            </div>
          </div>
          <div className="m-detail-row">
            <span className="m-detail-label">Dates</span>
            <div className="m-detail-value m-dates">
              <span>Start: <strong>{formatDate(item.startDate)}</strong></span>
              <span>Due: <strong>{formatDate(item.dueDate)}</strong></span>
            </div>
          </div>
          <div className="m-detail-row">
            <span className="m-detail-label">Progress</span>
            <div className="m-detail-value m-progress-bar">
              <div className="m-progress-track">
                <div className="m-progress-fill" style={{ width: `${item.progress || 0}%` }} />
              </div>
              <span className="m-progress-text">{item.progress || 0}%</span>
            </div>
          </div>

          {item.subtasks && item.subtasks.length > 0 && (
            <div className="m-detail-row" style={{ alignItems: 'start', gridTemplateColumns: '86px 1fr' }}>
              <span className="m-detail-label">Subtasks</span>
              <div className="m-detail-value" style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {item.subtasks.map((s, idx) => (
                  <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                    <input
                      type="checkbox"
                      checked={s.completed || s.status === 'Done'}
                      readOnly
                      style={{ width: 16, height: 16 }}
                    />
                    <span style={{ flex: 1, textDecoration: (s.completed || s.status === 'Done') ? 'line-through' : 'none', color: (s.completed || s.status === 'Done') ? 'var(--text-secondary)' : 'inherit' }}>
                      {s.name}
                    </span>
                    <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{s.progress || 0}%</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Finance details */}
          {hasFinance && (
            <div className="m-finance-block">
              <div className="m-finance-row">
                <span>Cost</span>
                <strong>{formatMoney(value, cur)}</strong>
              </div>
              <div className="m-finance-row">
                <span>Paid for this task</span>
                <strong style={{ color: '#1DBF73' }}>{formatMoney(paid, cur)}</strong>
              </div>
              <div className="m-finance-row">
                <span>Remaining to pay</span>
                <strong style={{ color: remaining > 0 ? '#E24B5B' : '#1DBF73' }}>{formatMoney(remaining, cur)}</strong>
              </div>
            </div>
          )}

          <div className="m-task-actions">
            <button className="btn btn-secondary btn-sm" onClick={() => onEdit && onEdit(item, 'finance')} data-testid="mobile-task-add-cost-btn">
              <FiDollarSign size={14} /> {hasFinance ? 'Manage Payments' : 'Add Cost'}
            </button>
            <button className="btn btn-secondary btn-sm" onClick={() => onEdit && onEdit(item)}>
              <FiEdit2 size={14} /> Edit Details
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const MobileBoardTaskList = ({ group, token, onEdit, onDelete, onUpdate, boardBaseCurrency, paymentsByItem = {} }) => {
  return (
    <div className="m-task-list">
      {(group.items || []).map((item) => (
        <MobileTaskRow
          key={item.id}
          item={item}
          token={token}
          onEdit={onEdit}
          onDelete={onDelete}
          onUpdate={onUpdate}
          boardBaseCurrency={boardBaseCurrency}
          paymentSummary={paymentsByItem[item.id]}
        />
      ))}
    </div>
  );
};

export default MobileBoardTaskList;
